import { PayloadAction, createSlice, isAnyOf } from '@reduxjs/toolkit';
import { logger } from 'common/services';
import { toBoolean, toTimestamp } from 'common/utils';
import { differenceInMilliseconds } from 'date-fns';
import { ActiveMeetingBroadcast, AgoraCloudPlayerInfo, BroadcastConnectionInfo, ClientRole } from 'domain/Broadcast';
import {
  EventMetricsPayload,
  EventRoom,
  EventRoomApiPayload,
  EventRoomAttendee,
  MeetingGuest,
  MeetingGuestApiPayload,
  TranscriptionMode,
} from 'domain/event';
import {
  mapBackstageRoomFromApi,
  mapEventRoomFromApi,
  mapToEventRoomAttendeeFromApi,
  mapToMeetingGuestFromApi,
} from 'event/mappers';
import { isEqual, uniq, uniqBy } from 'lodash-es';
import { eventRoomActions } from 'pages/EventRoom/state';
import { EventStateSnapshot, eventApi } from 'store/apis/event';

import {
  backstageActions,
  broadcastActions,
  broadcastChatModerationToggled_WS,
  broadcastDemoted_WS,
  broadcastGuestDemoted_WS,
  broadcastGuestPromotedList_WS,
  broadcastPromoted_WS,
  broadcastStarted_WS,
  broadcastVideoViaUrlPaused_WS,
  broadcastVideoViaUrlResumed_WS,
  dismissRateNatterExperienceAfterTimeout,
  recordingStarted_WS,
  recordingStopped_WS,
  screensharingStarted_WS,
  screensharingStopped_WS,
  stageInvitationAccepted_WS,
  stageInvitationReceived_WS,
  stageInvitationRejected_WS,
  stageInvitationSent_WS,
  stopScreensharing_WS,
} from './actions/broadcast.actions';
import {
  audienceCountUpdated_WS,
  bmmMeetingGuestHandDown_WS,
  bmmMeetingGuestHandUpList_WS,
  bmmMeetingGuestHandUp_WS,
  eventBigMeetingModeEnabled_WS,
  guestExitedMeeting_WS,
  meetingGuestHandDown_WS,
  meetingGuestHandUp_WS,
  meetingMetricsUpdated_WS,
  meetingStarted_WS,
  natterSnapshot_WS,
  natteringJoin_WS,
  natteringSkip_WS,
  questionAssignment_WS,
  submitCallFeedback,
} from './actions/meeting.actions';
import {
  addNewRoom_WS,
  cancelNatterSupport,
  guestJoinedRoom_WS,
  joinRoomAsSupport,
  joinRoom_WS,
  leaveRoomAsSupport,
  meetingBroadcastCountdownStarted_WS,
  meetingRoomClosed_WS,
  natterEnded_WS,
  natteringActions,
  natteringStarted_WS,
  requestNatterSupport,
  roomExit_WS,
  roomSupportCancelled_WS,
  roomSupportJoined_WS,
  roomSupportRequestedList_WS,
  roomSupportRequested_WS,
  roomsCountUpdated_WS,
  transcriptionStarted_WS,
  userDisconnectedList_WS,
} from './actions/room.actions';
import { ActiveRoomState, EventPhase, EventState, NatteringPhase, NatteringStatus } from './types/event.types';
import { findRoomById } from './utils';

const initialEventMetricsState = {
  conversations: null,
  dataPoints: null,
  ratingAverage: null,
  activeParticipants: null,
  wordsTranscribed: null,
  sequenceId: 0,
};

export const initialEventState: EventState = {
  activeEventId: undefined,
  broadcast: null,
  recordingArmed: false,
  serverTimeOffset: 0,
  activeRoom: null,
  rooms: [],
  displayRoomHelpCenter: false,
  roomsRequestingSupport: [],
  roomsCount: 0,
  pin: '',
  screensharingDetails: null,
  isScreensharing: false,
  metrics: initialEventMetricsState,
  audience: [],
  audienceCount: 0,
  speakers: [],
  participantsInvitedToStage: [],
  participantsWithRaisedHands: [],
  participantsDisconnected: [],
  stageInvitation: null,
  lastCallGuests: [],
  isJoiningNattering: false,
  natteringRoundStartTime: undefined,
  natteringRoundEndTime: undefined,
  natteringRoundTopic: undefined,
  natteringRoundId: undefined,
  isBigMeetingMode: false,
  showAudienceList: true,
  activePanelTab: 0,
  isWaitingForNattering: false,
  questionsAssignments: [],
  activeSpeakersIds: [],
  roomIdToRate: null,
  eventPhase: EventPhase.WaitingRoom,
  natteringPhase: NatteringPhase.WaitingToStart,
  natteringStatus: NatteringStatus.Initial,
  rematchCountdown: null,
  natteringResumeCountdown: null,
  natteringSkipConfirmActive: false,
  isMatchingTeleprompterMounted: false,
};

const emptyBroadcast: ActiveMeetingBroadcast = {
  broadcastChatToken: '',
  channelId: '',
  cloudPlayer: null,
  isChatModerated: false,
  publisherAccessToken: null,
  role: ClientRole.Audience,
  screensharing: undefined,
  accessToken: undefined,
  isRecording: false,
};

const defaultActiveRoomState: ActiveRoomState = {
  id: '',
  isBroadcastCountdown: false,
  joinedAsSupport: false,
  showTranscriptionModal: false,
  isTranscribing: true,
};

const handleGuestExitMeeting = (attendee: MeetingGuest, state: EventState) => {
  const audienceIndex = state.audience.findIndex(({ id }) => id === attendee.id);
  if (audienceIndex > -1) {
    state.audience.splice(audienceIndex, 1);
  }

  const attendeeIndex = state.speakers.findIndex(({ id }) => id === attendee.id);
  if (attendeeIndex > -1) {
    state.speakers.splice(attendeeIndex, 1);
  }

  state.participantsDisconnected.push(attendee.id);
  handChangeHandler(state, false, attendee.id, attendee.room);
};

const handChangeHandler = (state: EventState, isHandRaised: boolean, userId: number, roomId?: string) => {
  if (isHandRaised) {
    if (state.broadcast) {
      state.participantsWithRaisedHands.push(userId);
    } else if (roomId) {
      state.roomsRequestingSupport.push(roomId);
    }
  } else if (state.broadcast) {
    state.participantsWithRaisedHands = state.participantsWithRaisedHands.filter((id) => id !== userId);
  } else if (roomId) {
    state.roomsRequestingSupport = state.roomsRequestingSupport.filter((id) => id !== roomId);
  }
};

const handChangeHandlerBMM = (state: EventState, isHandRaised: boolean, guestDataList: MeetingGuestApiPayload[]) => {
  guestDataList.forEach((guestData) => {
    const guest = mapToMeetingGuestFromApi(guestData);
    handChangeHandler(state, isHandRaised, guest.id, guest.room);
    const isInSpeakers = state.speakers.some((s) => s.id === guest.id);
    const indexOfAudience = state.audience.findIndex((s) => s.id === guest.id);
    const isInAudience = indexOfAudience > -1;
    const isMemberInState = isInSpeakers || isInAudience;
    if (!isMemberInState && isHandRaised) {
      state.audience.unshift(guest);
    }
  });
};

const joinRoomHandler = (state: EventState, payload: EventRoomApiPayload) => {
  const roomData = mapEventRoomFromApi(payload);
  const existingRoom = findRoomById(state.rooms, payload.id);
  if (existingRoom) {
    existingRoom.accessToken = roomData.accessToken;
    existingRoom.duration = roomData.duration;
    existingRoom.startTime = roomData.startTime;
    existingRoom.endTime = roomData.endTime;
    existingRoom.id = roomData.id;
    existingRoom.roomQuestionAssignments = roomData.roomQuestionAssignments;
  } else {
    state.rooms.push(roomData);
  }

  if (!state.activeRoom || state.activeRoom.id !== payload.id) {
    state.activeRoom = {
      ...defaultActiveRoomState,
      id: payload.id,
      showTranscriptionModal:
        payload.transcriptionMode === TranscriptionMode.OPTIONAL && !payload.hasSetTranscriptionPreference,
      isTranscribing: !!payload.isTranscribing,
    };
  }
};

const addNewRoomHandler = (state: EventState, payload: EventRoomApiPayload) => {
  const mapped = mapEventRoomFromApi(payload);
  const existingRoom = findRoomById(state.rooms, payload.id);
  if (existingRoom) {
    existingRoom.accessToken = mapped.accessToken;
    existingRoom.startTime = mapped.startTime;
    existingRoom.endTime = mapped.endTime;
    existingRoom.duration = mapped.duration;
    existingRoom.roomQuestionAssignments = mapped.roomQuestionAssignments;
    return;
  }
  state.rooms = uniqBy([...state.rooms, mapped], 'id');
};

export const eventSlice = createSlice({
  name: 'event',
  initialState: initialEventState,
  reducers: {
    setEventState: (state, { payload }: PayloadAction<EventStateSnapshot>) => {
      const { phase } = payload;
      if (phase.type !== 'Backstage') return;

      state.natteringRoundEndTime = phase.natteringRoundEndTime;
      state.natteringRoundTopic = phase.natteringTopic;

      // The old BE returns Backstage with only the four legacy fields. Without the rest of the
      // payload the broadcast WS chain that populates speakers/rooms isn't wired up either, so
      // flipping to Nattering here would strand the host in an empty backstage shell. Stay out of
      // the way and let the WS WaitingRoomEvent drive the UI — matches current prod behavior.
      // Drop this guard once the BE is permanently rolled out.
      if (phase.natteringRoundStartTime === undefined) return;

      state.eventPhase = EventPhase.Nattering;
      state.natteringPhase = NatteringPhase.InProgress;
      state.natteringRoundStartTime = phase.natteringRoundStartTime;
      state.isBigMeetingMode = phase.isBigMeetingMode ?? state.isBigMeetingMode;

      // The Backstage phase is owner-only; agoraAccessToken carries the publisher token, which we
      // route into both the join slot and the publisher slot so the host promotes to speaker.
      state.broadcast = {
        ...(state.broadcast ?? emptyBroadcast),
        accessToken: phase.agoraAccessToken,
        channelId: phase.agoraChannelId,
        role: ClientRole.Host,
        publisherAccessToken: phase.agoraAccessToken,
      };

      state.roomsRequestingSupport = phase.roomsRequestingSupport ?? state.roomsRequestingSupport;

      if (phase.rooms) {
        state.rooms = phase.rooms.map(mapBackstageRoomFromApi);
      }
    },
    setActiveEventId: (state, { payload }: PayloadAction<number>) => {
      state.activeEventId = payload;
    },
    clearPinValue: (state) => {
      state.pin = '';
    },
    addLastCallQuest: (state, { payload }: PayloadAction<EventRoomAttendee>) => {
      state.lastCallGuests = uniqBy([...state.lastCallGuests, payload], 'id');
    },
    addAudience: (state, action: PayloadAction<MeetingGuest[]>) => {
      const speakersIds = state.speakers.map((s) => s.id);
      const notSpeakers = action.payload.filter((a) => !speakersIds.includes(a.id));
      state.audience = uniqBy(notSpeakers.concat(state.audience), 'id');
    },
    addUserToAudience: (state, { payload }: PayloadAction<MeetingGuest>) => {
      state.audience.unshift(payload);
    },
    demoteUserToAudience: (state, { payload }: PayloadAction<{ newAudience: MeetingGuest[] }>) => {
      state.participantsInvitedToStage = [];
      if (state.isBigMeetingMode) {
        state.audience = payload.newAudience;
        state.participantsWithRaisedHands = [];
      }
    },
    removeUserFromAudience: (state, { payload }: PayloadAction<{ id: number }>) => {
      const indexOfAudience = state.audience.findIndex(({ id }) => id === payload.id);
      const isInAudience = indexOfAudience > -1;

      if (isInAudience) {
        state.audience.splice(indexOfAudience, 1);
      }
    },
    setDisconnectedParticipants: (state, action: PayloadAction<number[]>) => {
      state.participantsDisconnected = action.payload;
    },
    clearStageInvitation: (state) => {
      state.stageInvitation = null;
    },
    setCloudPlayer: (state, { payload }: PayloadAction<AgoraCloudPlayerInfo | null>) => {
      state.broadcast = {
        ...(state.broadcast ?? emptyBroadcast),
        cloudPlayer: payload,
      };
    },
    setUserHandRaisedForUser: (state, { payload }: PayloadAction<{ id?: number; handRaised: boolean }>) => {
      if (payload.id) {
        handChangeHandler(state, payload.handRaised, payload.id);
      }
    },
    addRoom: (state, { payload: { room } }: PayloadAction<{ room: EventRoom }>) => {
      state.rooms.unshift(room);
    },
    removeRoom: (state, { payload: { roomId } }: PayloadAction<{ roomId: string }>) => {
      state.rooms = state.rooms.filter((r) => r.id !== roomId);
    },
    updateRoom: (state, { payload: { room } }: PayloadAction<{ room: EventRoom }>) => {
      const roomIndex = state.rooms.findIndex(({ id }) => id === room.id);
      state.rooms[roomIndex] = room;
    },
    addParticipantsWithRaisedHand: (state, { payload }: PayloadAction<number[]>) => {
      state.participantsWithRaisedHands = [...new Set(state.participantsWithRaisedHands.concat(payload))];
    },
    removeParticipantsWithRaisedHand: (state, { payload }: PayloadAction<number[]>) => {
      state.participantsWithRaisedHands = state.participantsWithRaisedHands.filter((id) => !payload.includes(id));
    },
    toggleShowAudienceList: (state) => {
      state.showAudienceList = !state.showAudienceList;
    },
    setActivePanelTab: (state, { payload }: PayloadAction<number>) => {
      state.activePanelTab = payload;
    },
    hideTranscriptionModal: (state) => {
      if (!state.activeRoom) return;

      state.activeRoom.showTranscriptionModal = false;
    },
    setActiveSpeakersIds: (state, { payload }: PayloadAction<number[]>) => {
      if (!isEqual(payload, state.activeSpeakersIds)) {
        state.activeSpeakersIds = payload;
      }
    },
    toggleRoomHelpCenter: (state) => {
      state.displayRoomHelpCenter = !state.displayRoomHelpCenter;
    },
    setRematchCountdown: (state, { payload }: PayloadAction<{ endsAt: number } | null>) => {
      state.rematchCountdown = payload;
    },
    setNatteringResumeCountdown: (state, { payload }: PayloadAction<{ endsAt: number } | null>) => {
      state.natteringResumeCountdown = payload;
    },
    setMatchingTeleprompterMounted: (state, { payload }: PayloadAction<boolean>) => {
      state.isMatchingTeleprompterMounted = payload;
    },
    meetingMetricsUpdated: (state, action: PayloadAction<EventMetricsPayload>) => {
      // Merge updates to avoid overwriting concurrent WebSocket updates
      // Only update fields that are explicitly provided (not null/undefined)
      if (action.payload.conversations !== undefined && action.payload.conversations !== null) {
        state.metrics.conversations = action.payload.conversations;
      }
      if (action.payload.dataPoints !== undefined && action.payload.dataPoints !== null) {
        state.metrics.dataPoints = action.payload.dataPoints;
      }
      if (action.payload.ratingAverage !== undefined && action.payload.ratingAverage !== null) {
        state.metrics.ratingAverage = action.payload.ratingAverage;
      }
      if (action.payload.activeParticipants !== undefined && action.payload.activeParticipants !== null) {
        state.metrics.activeParticipants = action.payload.activeParticipants;
      }
      if (action.payload.wordsTranscribed !== undefined && action.payload.wordsTranscribed !== null) {
        state.metrics.wordsTranscribed = action.payload.wordsTranscribed;
      }
      // Always update sequenceId if provided to track ordering
      if (action.payload.sequenceId !== undefined) {
        state.metrics.sequenceId = action.payload.sequenceId;
      }
    },
  },
  extraReducers: (builder) =>
    builder
      .addCase(addNewRoom_WS, (state, { payload }) => {
        addNewRoomHandler(state, payload);
      })
      .addCase(joinRoom_WS, (state, { payload }) => {
        joinRoomHandler(state, payload);
        state.eventPhase = EventPhase.Nattering;
        state.natteringPhase = NatteringPhase.InProgress;
        // Being placed in a room means we were matched, not skipped. Reset the status so that a
        // match landing in the same instant a user taps Skip (which optimistically sets Skipped)
        // deterministically wins and routes them to the room rather than the skipped screen.
        state.natteringStatus = NatteringStatus.Accepted;
        // Matched into a room — clear the flag set in natteringStarted_WS so the connection watchdog doesn't fire.
        state.isWaitingForNattering = false;
        // Safety net for joinRoom_WS arriving outside the buffered rematch path; the middleware
        // already dispatches setRematchCountdown(null) on the normal release, this just covers
        // anything that bypasses it.
        state.rematchCountdown = null;
      })
      .addCase(joinRoomAsSupport.fulfilled, (state, { meta }) => {
        state.activeRoom = {
          ...defaultActiveRoomState,
          id: meta.arg.room.id,
          joinedAsSupport: true,
        };
        state.natteringPhase = NatteringPhase.InProgress;
        state.natteringStatus = NatteringStatus.Accepted;

        const room = findRoomById(state.rooms, meta.arg.room.id);
        if (!room) {
          //in Big Meeting Mode its possible that the room is only present in search results
          state.rooms.unshift({
            ...meta.arg.room,
            roomQuestionAssignments: [],
          });
        }
      })
      .addCase(roomSupportJoined_WS, (state, { payload }) => {
        state.roomsRequestingSupport = state.roomsRequestingSupport.filter((roomId) => roomId !== payload.roomId);
        const room = findRoomById(state.rooms, payload.roomId);
        if (room) {
          const attendee = mapToEventRoomAttendeeFromApi(payload.user);
          room.attendees.push(attendee);
        }

        if (!state.activeRoom) return;

        state.activeRoom.isTranscribing = !!payload.isTranscribing;
      })
      .addCase(guestJoinedRoom_WS, (state) => {
        state.isWaitingForNattering = false;
      })
      .addCase(guestExitedMeeting_WS, (state, { payload }) => {
        const attendee = mapToMeetingGuestFromApi(payload);
        handleGuestExitMeeting(attendee, state);
      })
      .addCase(submitCallFeedback.fulfilled, (state) => {
        state.activeRoom = null;
        state.lastCallGuests = [];
        state.roomIdToRate = null;
      })
      .addCase(dismissRateNatterExperienceAfterTimeout, (state) => {
        state.roomIdToRate = null;
      })
      .addCase(leaveRoomAsSupport.fulfilled, (state) => {
        if (state.isBigMeetingMode) {
          state.rooms = state.rooms.filter(
            (r) => r.id !== state.activeRoom?.id || state.roomsRequestingSupport.includes(r.id)
          );
        }
        state.activeRoom = null;
        state.lastCallGuests = [];
      })
      .addCase(meetingStarted_WS, (state, action) => {
        const offset = differenceInMilliseconds(new Date(), new Date(action.payload.serverNow));
        state.serverTimeOffset = offset;
      })
      .addCase(meetingRoomClosed_WS, (state, action) => {
        state.rooms = state.rooms.filter(({ id }) => id !== action.payload.id);
      })
      .addCase(meetingBroadcastCountdownStarted_WS, (state) => {
        if (state.activeRoom) {
          state.activeRoom.isBroadcastCountdown = true;
        }
      })
      .addCase(broadcastStarted_WS, (state, action: PayloadAction<BroadcastConnectionInfo>) => {
        const { accessToken, broadcastChatToken, channelId, isChatModerated, isRecording } = action.payload;
        state.broadcast = {
          ...(state.broadcast ?? emptyBroadcast),
          accessToken,
          broadcastChatToken,
          channelId,
          isChatModerated: toBoolean(isChatModerated),
          // Reconcile against recordingArmed: a `recording_started` that landed before this
          // message (different STOMP destination, no ordering guarantee) would otherwise be lost.
          isRecording: toBoolean(isRecording) || state.recordingArmed,
        };
        state.eventPhase = EventPhase.Broadcast;

        if (state.activeRoom) {
          state.activeRoom.isBroadcastCountdown = false;
        }

        state.questionsAssignments = [];
        state.natteringRoundStartTime = undefined;
        state.natteringRoundEndTime = undefined;
        state.natteringRoundTopic = undefined;
        state.roomsRequestingSupport = [];
        // The buffered-resume countdown (if any) is done the moment the broadcast actually mounts;
        // the middleware clears it on release, this covers a broadcastStarted_WS that bypasses it.
        state.natteringResumeCountdown = null;
      })
      .addCase(broadcastActions.broadcastStopped, (state) => {
        state.broadcast = null;
        // Clear armed state at the broadcast→nattering transition so the indicator can't leak into
        // backstage or carry a stale value into the next segment (recording re-arms per segment).
        state.recordingArmed = false;
        state.audience = [];
        state.speakers = [];
        state.participantsWithRaisedHands = [];
        state.participantsInvitedToStage = [];
        state.isWaitingForNattering = true;
        state.natteringStatus = NatteringStatus.ToBeDecided;
      })
      .addCase(broadcastActions.backstageStarted, (state, { payload }) => {
        // Host's BackstageV2 teardown path (dispatched instead of broadcastStopped for hosts).
        // Reset armed state here too, otherwise the indicator can leak into backstage / the next
        // segment for hosts, who never hit the broadcastStopped reset.
        state.recordingArmed = false;
        if (state.broadcast) {
          state.broadcast.isRecording = false;
        }
        state.eventPhase = EventPhase.Nattering;
        state.audience = state.audience.filter(({ id }) => payload.hosts.some(({ accountId }) => id === accountId));
        state.speakers = state.speakers.filter(({ id }) => payload.hosts.some(({ accountId }) => id === accountId));
        state.participantsWithRaisedHands = [];
        state.participantsInvitedToStage = [];
      })
      .addCase(
        natteringStarted_WS,
        (state, { payload: { natteringRoundStartTime, natteringRoundEndTime, natteringRoundId, topics } }) => {
          state.natteringRoundStartTime = toTimestamp(natteringRoundStartTime);
          state.natteringRoundEndTime = toTimestamp(natteringRoundEndTime);
          state.natteringRoundId = natteringRoundId;
          // `topics[0]` is the shared round topic — all rooms in a round share one topic for now.
          // If per-room topics land, this needs to become a roomId/questionId -> topic map.
          state.natteringRoundTopic = topics?.[0];
          state.eventPhase = EventPhase.Nattering;
          state.natteringPhase = NatteringPhase.WaitingToStart;
          // Late joiners/reloaders miss the broadcastStopped that flips this; reset so both round-entry paths (accept/skip modal + JIT teleprompter) can open and opt into the pool.
          state.natteringStatus = NatteringStatus.ToBeDecided;
          // The round has started but we have not been matched into a room yet. Mark the user as
          // waiting so `selectIsMeetingStateUnknown` resolves to "known" — otherwise the connection
          // watchdog mistakes the pre-match window for a failed channel join and shows a spurious
          // "Failed to join the channel" error. Cleared again once `joinRoom_WS` places us in a room.
          state.isWaitingForNattering = true;
          // Reset metrics to avoid showing stale data from previous round
          state.metrics = {
            ...initialEventMetricsState,
            sequenceId: state.metrics.sequenceId, // Preserve existing sequenceId
          };
        }
      )
      .addCase(broadcastActions.natteringStarted, (state) => {
        state.rooms = [];
        state.questionsAssignments = [];
        state.roomIdToRate = null;
      })
      .addCase(roomExit_WS, (state, { payload }) => {
        state.activeRoom = null;
        state.roomIdToRate = payload.roomId;
      })
      .addCase(meetingMetricsUpdated_WS, (state, action) => {
        // Merge updates to avoid overwriting concurrent WebSocket updates
        // Only update fields that are explicitly provided (not null/undefined)
        if (action.payload.conversations !== undefined && action.payload.conversations !== null) {
          state.metrics.conversations = action.payload.conversations;
        }
        if (action.payload.dataPoints !== undefined && action.payload.dataPoints !== null) {
          state.metrics.dataPoints = action.payload.dataPoints;
        }
        if (action.payload.ratingAverage !== undefined && action.payload.ratingAverage !== null) {
          state.metrics.ratingAverage = action.payload.ratingAverage;
        }
        if (action.payload.activeParticipants !== undefined && action.payload.activeParticipants !== null) {
          state.metrics.activeParticipants = action.payload.activeParticipants;
        }
        if (action.payload.wordsTranscribed !== undefined && action.payload.wordsTranscribed !== null) {
          state.metrics.wordsTranscribed = action.payload.wordsTranscribed;
        }
        // Always update sequenceId if provided to track ordering
        if (action.payload.sequenceId !== undefined) {
          state.metrics.sequenceId = action.payload.sequenceId;
        }
      })
      .addCase(screensharingStarted_WS, (state, action) => {
        state.screensharingDetails = {
          sharerName: action.payload.sharerName,
          sharerUID: Number(action.payload.sharerUID),
        };
      })
      .addCase(screensharingStopped_WS, (state) => {
        state.screensharingDetails = null;
        state.isScreensharing = false;
      })
      .addCase(stopScreensharing_WS.action, (state) => {
        state.screensharingDetails = null;
      })
      .addCase(stageInvitationReceived_WS, (state, action) => {
        state.stageInvitation = action.payload;
      })
      .addCase(stageInvitationSent_WS, (state, { payload }) => {
        state.participantsInvitedToStage.push(Number(payload.invitedUserId));
      })
      .addCase(stageInvitationAccepted_WS, (state, { payload }) => {
        state.participantsInvitedToStage = state.participantsInvitedToStage.filter(
          (id) => id !== Number(payload.invitedUserId)
        );
      })
      .addCase(stageInvitationRejected_WS, (state, { payload }) => {
        state.participantsInvitedToStage = state.participantsInvitedToStage.filter(
          (id) => id !== Number(payload.invitedUserId)
        );
      })
      .addCase(broadcastActions.promotedToSpeaker, (state, { payload: newSpeaker }) => {
        state.speakers = uniqBy(state.speakers.concat([newSpeaker]), 'id');
        state.audience = state.audience.filter(({ id }) => id !== newSpeaker.id);
      })
      .addCase(broadcastGuestPromotedList_WS, (state, action) => {
        const newSpeakers = action.payload.map(mapToMeetingGuestFromApi);
        const newSpeakersIds = newSpeakers.map((s) => s.id);
        state.speakers = uniqBy(state.speakers.concat(newSpeakers), 'id');
        state.audience = state.audience.filter(({ id }) => !newSpeakersIds.includes(id));
      })
      .addCase(broadcastPromoted_WS, (state, action) => {
        state.broadcast = {
          ...(state.broadcast ?? emptyBroadcast),
          role: ClientRole.Host,
          publisherAccessToken: action.payload.publisherAccessToken,
        };
      })
      .addCase(broadcastDemoted_WS, (state) => {
        state.broadcast = {
          ...(state.broadcast ?? emptyBroadcast),
          role: ClientRole.Audience,
          publisherAccessToken: null,
        };
      })
      .addCase(broadcastGuestDemoted_WS, (state, { payload }) => {
        const speaker = mapToMeetingGuestFromApi(payload);
        state.speakers = state.speakers.filter(({ id }) => id !== speaker.id);

        if (!state.isBigMeetingMode) {
          state.audience.push(speaker);
        }
      })
      .addCase(meetingGuestHandUp_WS, (state, { payload }) => {
        handChangeHandler(state, true, Number(payload.id), payload.room);
      })
      .addCase(meetingGuestHandDown_WS, (state, { payload }) => {
        handChangeHandler(state, false, Number(payload.id), payload.room);
      })
      .addCase(bmmMeetingGuestHandUp_WS, (state, { payload }) => {
        handChangeHandlerBMM(state, true, [payload]);
      })
      .addCase(bmmMeetingGuestHandUpList_WS, (state, { payload }) => {
        handChangeHandlerBMM(state, true, payload);
      })
      .addCase(bmmMeetingGuestHandDown_WS, (state, { payload }) => {
        handChangeHandlerBMM(state, false, [payload]);
      })
      .addCase(requestNatterSupport.fulfilled, (state, { meta }) => {
        state.roomsRequestingSupport.push(meta.arg.roomId);
      })
      .addCase(cancelNatterSupport.fulfilled, (state, { meta }) => {
        state.roomsRequestingSupport = state.roomsRequestingSupport.filter((roomId) => roomId !== meta.arg.roomId);
      })
      .addCase(roomSupportRequested_WS, (state, { payload }) => {
        state.roomsRequestingSupport = uniq([...state.roomsRequestingSupport, payload.room.id]);
      })
      .addCase(roomSupportRequestedList_WS, (state, { payload }) => {
        state.roomsRequestingSupport = state.roomsRequestingSupport.concat(payload.rooms.map(({ id }) => id));
      })
      .addCase(roomSupportCancelled_WS, (state, { payload }) => {
        state.roomsRequestingSupport = state.roomsRequestingSupport.filter((roomId) => roomId !== payload.roomId);
      })
      .addCase(broadcastChatModerationToggled_WS, (state, { payload }) => {
        state.broadcast = {
          ...(state.broadcast ?? emptyBroadcast),
          isChatModerated: payload.isChatModerated,
        };
      })
      .addCase(broadcastVideoViaUrlPaused_WS, (state) => {
        if (state.broadcast?.cloudPlayer) {
          state.broadcast.cloudPlayer.isPlaying = false;
        }
      })
      .addCase(broadcastVideoViaUrlResumed_WS, (state) => {
        if (state.broadcast?.cloudPlayer) {
          state.broadcast.cloudPlayer.isPlaying = true;
        }
      })
      .addCase(eventBigMeetingModeEnabled_WS, (state) => {
        state.isBigMeetingMode = true;
        state.rooms = state.rooms.filter((r) => r.id === state.activeRoom?.id);
        state.audience = state.audience.filter((a) => state.participantsWithRaisedHands.includes(a.id));
      })
      .addCase(audienceCountUpdated_WS, (state, { payload }) => {
        state.audienceCount = payload.audienceCount;
      })
      .addCase(roomsCountUpdated_WS, (state, { payload }) => {
        state.roomsCount = payload.roomsCount;
      })
      .addCase(userDisconnectedList_WS, (state, { payload }) => {
        state.participantsDisconnected = state.participantsDisconnected.concat(payload.map(({ userId }) => userId));
      })
      .addCase(recordingStarted_WS, (state) => {
        // Arm regardless of whether `broadcast` exists yet, so a `recording_started` that races
        // ahead of `broadcast_started` (host on auto-record) isn't dropped — broadcast_started
        // reconciles its seed against this flag.
        state.recordingArmed = true;
        if (state.broadcast) {
          state.broadcast.isRecording = true;
        }
      })
      .addCase(recordingStopped_WS, (state) => {
        state.recordingArmed = false;
        if (state.broadcast) {
          state.broadcast.isRecording = false;
        }
      })
      .addCase(transcriptionStarted_WS, (state) => {
        if (!state.activeRoom) return;

        state.activeRoom.isTranscribing = true;
      })
      .addCase(natteringJoin_WS.action, (state) => {
        state.natteringStatus = NatteringStatus.Accepted;
      })
      .addCase(questionAssignment_WS, (state, { payload }) => {
        state.questionsAssignments = payload.questionsAssignments;
        state.natteringPhase = NatteringPhase.InProgress;
        state.natteringStatus = NatteringStatus.Skipped;
        // TODO: used to set the nattering round end time for Automated Natters
        // state.natteringRoundEndTime = toTimestamp(payload.questionsAssignments[0]?.enabledUntil);
      })
      .addCase(natterSnapshot_WS, (state, { payload }) => {
        // TODO: remove this when we have a proper snapshot
        logger.info('natterSnapshot_WS', payload);
      })
      .addCase(natteringSkip_WS.action, (state) => {
        state.natteringStatus = NatteringStatus.Skipped;
      })
      .addCase(natteringActions.skipConfirm.opened, (state) => {
        state.natteringSkipConfirmActive = true;
      })
      .addCase(natteringActions.skipConfirm.closed, (state) => {
        state.natteringSkipConfirmActive = false;
      })
      .addCase(backstageActions.viewRequestsClicked, (state) => {
        state.activePanelTab = 1;
      })
      .addCase(broadcastActions.startScreenshare, (state) => {
        state.isScreensharing = true;
      })
      .addCase(broadcastActions.stopScreenshare, (state) => {
        state.isScreensharing = false;
      })
      .addMatcher(eventApi.endpoints.getEventDetails.matchFulfilled, (state, { meta }) => {
        state.activeEventId = meta.arg.originalArgs.id;
      })
      .addMatcher(eventApi.endpoints.validateEventPin.matchFulfilled, (state, { meta }) => {
        state.pin = meta.arg.originalArgs.pin;
      })
      .addMatcher(eventApi.endpoints.validateEventPin.matchRejected, (state, { meta }) => {
        state.pin = meta.arg.originalArgs.pin;
      })
      .addMatcher(isAnyOf(eventRoomActions.cleanedUp, natterEnded_WS), () => initialEventState),
});
